import { useState, useMemo, useCallback } from 'react'
import { Box, Flex, Button, Input, Tabs } from '@chakra-ui/react'
import { Search, Mail, Download, CreditCard } from 'lucide-react'
import {
  OwnerAccountsTable,
  FiscalYearSelector,
  OwnerAccountDetailDrawer,
  PaymentModal,
  SendPaymentLinkModal,
} from '../../components'
import type { UnitGroup } from '../../components/OwnerAccountsTable'
import type { PaymentFormData } from '../../components/PaymentModal'
import type { PaymentLinkFormData } from '../../components/SendPaymentLinkModal'
import { useAccountingContext } from '../../context/AccountingContext'
import { useOwnerAccounts, useCreatePayment } from '../../hooks'
import { useBankAccounts } from '@/features/banking/hooks/useBankAccounts'
import { toaster } from '@/shared/lib/toaster'
import type { OwnerBalance } from '../../types'

export function OwnerAccountsTab() {
  const { residenceId, activeFiscalYear, setSelectedFiscalYear } = useAccountingContext()
  const [searchTerm, setSearchTerm] = useState('')

  // État pour le drawer de détail
  const [selectedUnitGroup, setSelectedUnitGroup] = useState<UnitGroup | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // État pour la modale de règlement
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [paymentUnitGroup, setPaymentUnitGroup] = useState<UnitGroup | null>(null)

  // État pour la modale de lien de paiement
  const [isPaymentLinkModalOpen, setIsPaymentLinkModalOpen] = useState(false)
  const [paymentLinkUnitGroup, setPaymentLinkUnitGroup] = useState<UnitGroup | null>(null)
  const [paymentLinkOwner, setPaymentLinkOwner] = useState<OwnerBalance | null>(null)

  // Récupérer les données pour la navigation
  const { data: accounts } = useOwnerAccounts(residenceId)

  // Hook de création de règlement
  const createPayment = useCreatePayment()

  // Comptes bancaires pour récupérer le compte 512
  const { data: bankAccounts } = useBankAccounts({
    residence_id: residenceId,
    is_active: true,
  })

  // Construire la liste des groupes pour la navigation dans le drawer
  const unitGroups = useMemo(() => {
    if (!accounts) return []

    const groupMap = new Map<string, UnitGroup>()

    for (const account of accounts) {
      const key = account.unit_id

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          unitId: account.unit_id,
          unitNumber: account.unit_number,
          accountCode: account.account_code,
          accountId: account.account_id,
          owners: [],
          totalBalance: 0,
          hasMultipleOwners: false,
        })
      }

      const group = groupMap.get(key)!
      group.owners.push(account)
      group.totalBalance += account.balance || 0
    }

    for (const group of groupMap.values()) {
      group.hasMultipleOwners = group.owners.length > 1
    }

    const groups = Array.from(groupMap.values())
    groups.sort((a, b) => a.unitNumber.localeCompare(b.unitNumber, undefined, { numeric: true }))

    return groups
  }, [accounts])

  // Navigation dans le drawer
  const currentIndex = useMemo(() => {
    if (!selectedUnitGroup) return -1
    return unitGroups.findIndex((g) => g.unitId === selectedUnitGroup.unitId)
  }, [selectedUnitGroup, unitGroups])

  const handleNavigatePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setSelectedUnitGroup(unitGroups[currentIndex - 1] ?? null)
    }
  }, [currentIndex, unitGroups])

  const handleNavigateNext = useCallback(() => {
    if (currentIndex < unitGroups.length - 1) {
      setSelectedUnitGroup(unitGroups[currentIndex + 1] ?? null)
    }
  }, [currentIndex, unitGroups])

  // Gestionnaires d'actions
  const handleSelectUnit = useCallback((group: UnitGroup) => {
    setSelectedUnitGroup(group)
    setIsDrawerOpen(true)
  }, [])

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false)
  }, [])

  const handleNewPayment = useCallback((group: UnitGroup | null) => {
    if (group) {
      setPaymentUnitGroup(group)
      setIsPaymentModalOpen(true)
    }
  }, [])

  const handleSendPaymentLink = useCallback((group: UnitGroup | null, owner?: OwnerBalance) => {
    if (group) {
      setPaymentLinkUnitGroup(group)
      setPaymentLinkOwner(owner || null)
      setIsPaymentLinkModalOpen(true)
    }
  }, [])

  // Soumission du règlement
  const handlePaymentSubmit = async (data: PaymentFormData) => {
    if (!activeFiscalYear) {
      toaster.create({
        title: 'Erreur',
        description: 'Aucun exercice fiscal actif',
        type: 'error',
      })
      return
    }

    // Récupérer le compte 512 associé au compte bancaire sélectionné
    const selectedBankAccount = bankAccounts?.find((b) => b.id === data.bankAccountId)
    if (!selectedBankAccount?.residence_chart_account_id) {
      toaster.create({
        title: 'Erreur',
        description: 'Compte bancaire non configuré correctement',
        type: 'error',
      })
      return
    }

    // Convertir lettrageAmounts en tableau pour le hook
    const lettrageAmounts = Object.entries(data.lettrageAmounts)
      .filter(([_, amount]) => amount > 0)
      .map(([lineId, amount]) => ({ lineId, amount }))

    const result = await createPayment.mutateAsync({
      residenceId,
      fiscalYearId: activeFiscalYear.id,
      formData: data,
      bankAccount512Id: selectedBankAccount.residence_chart_account_id,
      lettrageAmounts: lettrageAmounts.length > 0 ? lettrageAmounts : undefined,
    })

    const formatCurrency = (value: number) =>
      value.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })

    let message = `Règlement de ${formatCurrency(data.amount)} enregistré`
    if (result.hasTransitAccount) {
      message += ` (via compte ${result.transitAccount})`
    }
    if (lettrageAmounts.length > 0) {
      const totalLettre = lettrageAmounts.reduce((s, la) => s + la.amount, 0)
      message += `. Lettrage de ${formatCurrency(totalLettre)} sur ${lettrageAmounts.length} appel(s) de fonds.`
    }

    toaster.create({
      title: 'Succès',
      description: message,
      type: 'success',
    })
  }

  // Envoi du lien de paiement Stripe
  const handlePaymentLinkSubmit = async (data: PaymentLinkFormData) => {
    // TODO: Implémenter l'appel à l'API Stripe pour créer et envoyer le lien
    // 1. Appeler l'edge function Supabase qui créera le Payment Link via Stripe API
    // 2. Envoyer l'email au copropriétaire
    console.log('Payment link data:', data)

    // Simuler un délai pour l'envoi
    await new Promise((resolve) => setTimeout(resolve, 1000))
  }

  return (
    <Box>
      <Tabs.Root defaultValue="all" variant="enclosed">
        <Flex justify="space-between" align="center" mb={4}>
          <Tabs.List>
            <Tabs.Trigger value="all">Tous les comptes</Tabs.Trigger>
            <Tabs.Trigger value="unpaid">Impayés</Tabs.Trigger>
            <Tabs.Trigger value="credit">Créditeurs</Tabs.Trigger>
          </Tabs.List>

          <Flex gap={2} align="center">
            <Button variant="outline" size="sm" onClick={() => handleNewPayment(selectedUnitGroup!)}>
              <CreditCard size={16} />
              Nouveau règlement
            </Button>
            <Button variant="outline" size="sm">
              <Mail size={16} />
              Relance groupée
            </Button>
            <Button variant="outline" size="sm">
              <Download size={16} />
              Export
            </Button>
            <FiscalYearSelector
              residenceId={residenceId}
              value={activeFiscalYear?.id}
              onChange={setSelectedFiscalYear}
            />
          </Flex>
        </Flex>

        <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
          {/* Recherche */}
          <Flex mb={4}>
            <Box position="relative" flex="1" maxW="400px">
              <Box
                position="absolute"
                left={3}
                top="50%"
                transform="translateY(-50%)"
                pointerEvents="none"
                color="gray.400"
              >
                <Search size={16} />
              </Box>
              <Input
                pl={10}
                placeholder="Rechercher un copropriétaire..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Box>
          </Flex>

          <Tabs.Content value="all">
            <OwnerAccountsTable
              residenceId={residenceId}
              onSelectUnit={handleSelectUnit}
              onNewPayment={handleNewPayment}
              onSendPaymentLink={handleSendPaymentLink}
            />
          </Tabs.Content>

          <Tabs.Content value="unpaid">
            <OwnerAccountsTable
              residenceId={residenceId}
              showOnlyUnpaid
              onSelectUnit={handleSelectUnit}
              onNewPayment={handleNewPayment}
              onSendPaymentLink={handleSendPaymentLink}
            />
          </Tabs.Content>

          <Tabs.Content value="credit">
            <Box textAlign="center" py={8} color="gray.500">
              Comptes créditeurs - à implémenter
            </Box>
          </Tabs.Content>
        </Box>
      </Tabs.Root>

      {/* Drawer de détail du compte */}
      <OwnerAccountDetailDrawer
        unitGroup={selectedUnitGroup}
        residenceId={residenceId}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        onNavigatePrevious={handleNavigatePrevious}
        onNavigateNext={handleNavigateNext}
        hasPrevious={currentIndex > 0}
        hasNext={currentIndex < unitGroups.length - 1}
        onNewPayment={handleNewPayment}
        onSendPaymentLink={handleSendPaymentLink}
      />

      {/* Modale de règlement */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        residenceId={residenceId}
        unitGroup={paymentUnitGroup}
        onSubmit={handlePaymentSubmit}
      />

      {/* Modale de lien de paiement */}
      <SendPaymentLinkModal
        isOpen={isPaymentLinkModalOpen}
        onClose={() => setIsPaymentLinkModalOpen(false)}
        residenceId={residenceId}
        unitGroup={paymentLinkUnitGroup}
        selectedOwner={paymentLinkOwner}
        onSubmit={handlePaymentLinkSubmit}
      />
    </Box>
  )
}
